import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { RouterModule, PreloadAllModules } from '@angular/router';
import { FlexLayoutModule } from '@angular/flex-layout';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatRadioModule } from '@angular/material/radio';
import { TreeNgxModule } from 'tree-ngx';
import { ClipboardModule } from 'ngx-clipboard';
import { MatExpansionModule } from '@angular/material/expansion';
import { LayoutModule } from '@angular/cdk/layout';
import { MatCheckboxModule, MAT_CHECKBOX_CLICK_ACTION } from '@angular/material/checkbox';
import { NgxDatatableModule } from '@swimlane/ngx-datatable';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { TranslateModule, TranslateLoader, MissingTranslationHandler } from '@ngx-translate/core';
import { MissingTranslation } from './services/missingTranslationHandler';
import { TranslationLoader } from './services/translationLoader';

/*
 * Platform and Environment providers/directives/pipes
 */
import { environment } from 'environments/environment';
import { ROUTES } from './app.routes';
// App is our top level component
import { AppComponent } from './app.component';
import { NgxPayPalModule } from 'ngx-paypal';
import { APP_RESOLVER_PROVIDERS } from './app.resolver';
import { AppState, InternalStateType } from './app.service';
import { AboutComponent } from './about';
import { SysreqComponent } from './sysreq';
import { PaypalComponent } from './pp';
import { MapEditorComponent } from './admin/map-editor';
import { InvoicesComponent } from './admin/invoices';
import { KeysComponent } from './admin/keys';
import { AdminComponent } from './admin';
import { ChangelogComponent } from './changelog';
import { NoContentComponent } from './no-content';
import { DevModuleModule } from './+dev-module';
import { MapsComponent } from './maps';
import { ProComponent } from './pro';

import '../styles/styles.scss';
import '../styles/headings.css';
import { GetZincComponent } from './getZinc';
import { LoginComponent } from './user/login/login.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { AuthGuard } from './auth.guard';
import { JwtInterceptor } from './jwt.interceptor';
import { ErrorInterceptor } from './error.interceptor';

// Application wide providers
const APP_PROVIDERS = [
    ...APP_RESOLVER_PROVIDERS,
    AppState
];

type StoreType = {
    state: InternalStateType,
    restoreInputValues: () => void,
    disposeOldHosts: () => void
};

/**
 * `AppModule` is the main entry point into Angular2's bootstraping process
 */
@NgModule({
    bootstrap: [AppComponent],
    declarations: [
        AppComponent,
        AboutComponent,
        SysreqComponent,
        AdminComponent,
        PaypalComponent,
        InvoicesComponent,
        MapEditorComponent,
        KeysComponent,
        GetZincComponent,
        NoContentComponent,
        ChangelogComponent,
        MapsComponent,
        ProComponent,
        LoginComponent
    ],
    /**
     * Import Angular's modules.
     */
    imports: [
        BrowserModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useClass: TranslationLoader
            }
        }),
        BrowserAnimationsModule,
        FormsModule,
        ReactiveFormsModule,
        LayoutModule,
        NgxPayPalModule,
        MatExpansionModule,
        MatCheckboxModule,
        ClipboardModule,
        MatButtonModule,
        NgxDatatableModule,
        MatSelectModule,
        TreeNgxModule,
        MatIconModule,
        MatRadioModule,
        MatFormFieldModule,
        MatInputModule,
        FlexLayoutModule,
        MatMenuModule,
        HttpClientModule,
        RouterModule.forRoot(ROUTES, {
            useHash: Boolean(history.pushState) === false,
            preloadingStrategy: PreloadAllModules
        }),

        /**
         * This section will import the `DevModuleModule` only in certain build types.
         * When the module is not imported it will get tree shaked.
         * This is a simple example, a big app should probably implement some logic
         */
        ...environment.showDevModule ? [DevModuleModule] : [],
    ],

    /**
     * Expose our Services and Providers into Angular's dependency injection.
     */
    providers: [
        environment.ENV_PROVIDERS,
        APP_PROVIDERS,
        MatIconRegistry,
        {
            provide: MissingTranslationHandler,
            useClass: MissingTranslation
        },
        { provide: MAT_CHECKBOX_CLICK_ACTION, useValue: 'check' },
        AuthGuard,
        { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    ]
})
export class AppModule { }



// WEBPACK FOOTER //
// ./src/app/app.module.ts